import { MapPinned, Pencil, Save } from 'lucide-react';
import type {
  FidsCoordinate,
  FidsRingType,
  FidsRingTypeOption,
  FidsTrackingRoute,
  FidsTrackingRouteStatus,
  FidsTrackingRouteSummary,
  FidsTrackingRouteWarehouse,
} from 'contracts/FidsTrackingRoutes';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ConfirmationDialog,
  type ConfirmationDialogRef,
} from 'components/Shared/ConfirmationDialog';
import { ListingPageHeader } from 'components/Shared';
import { apiErrorHandler, notify } from 'services';
import { Scaffold } from 'layouts';
import {
  activateFidsTrackingRoute,
  createFidsTrackingRoute,
  deactivateFidsTrackingRoute,
  deleteFidsTrackingRoute,
  duplicateFidsTrackingRoute,
  getFidsTrackingRoute,
  listFidsRingTypes,
  listFidsTrackingRoutes,
  updateFidsTrackingRoute,
} from 'hooks/api/useFidsTrackingRoutes';
import * as S from './styles';
import { MapCanvas } from './MapCanvas';
import { STATUS_LABELS, TrackingRouteList } from './TrackingRouteList';
import { LinkedWarehouses } from './LinkedWarehouses';
import { TrackingRouteActions } from './TrackingRouteActions';
import { NewTrackingRouteDialog } from './NewTrackingRouteDialog';
import { ReviewStep } from './ReviewStep';
import { RingLegend } from './RingLegend';
import { type RouteStep, RouteSteps, StepPanel } from './RouteSteps';
import { WelcomePanel } from './WelcomePanel';

const PAGE_TITLE = 'Mapeamento de checkpoints';

type RouteDetails = {
  name: string;
  description: string;
  center: FidsCoordinate | null;
  warehouses: FidsTrackingRouteWarehouse[];
};

const detailsOf = (route: FidsTrackingRoute): RouteDetails => ({
  name: route.name,
  description: route.description ?? '',
  center: route.center,
  warehouses: route.warehouses,
});

const sameDetails = (first: RouteDetails, second: RouteDetails) =>
  first.name === second.name &&
  first.description === second.description &&
  first.center?.latitude === second.center?.latitude &&
  first.center?.longitude === second.center?.longitude &&
  first.warehouses.map(({ id }) => id).join() ===
    second.warehouses.map(({ id }) => id).join();

// O passo que ainda falta; com tudo pronto, a revisão.
const firstPendingStep = ({ center, warehouses }: RouteDetails): RouteStep => {
  if (!center) return 'center';
  if (!warehouses.length) return 'warehouses';
  return 'review';
};

// O que falta para ativar, ou null.
const activationIssueOf = ({
  center,
  warehouses,
}: RouteDetails): string | null => {
  if (!center) return 'Marque o centro no mapa.';
  if (!warehouses.length) return 'Vincule pelo menos 1 armazém.';
  return null;
};

interface EditorProps {
  routeId: number;
  routesPanel: ReactNode;
  ringTypes: FidsRingTypeOption[];
  // Aviso da página (os anéis não carregaram), mostrado junto dos erros do editor.
  notice: ReactNode;
  onDirtyChange: (hasChanges: boolean) => void;
  onSaved: () => void;
  onDuplicated: (id: number) => void;
  onDeleted: () => void;
}

const EMPTY_DETAILS: RouteDetails = {
  name: '',
  description: '',
  center: null,
  warehouses: [],
};

// Edita o rastreio selecionado em 3 passos: centro, armazéns e revisão.
const TrackingRouteEditor: React.FC<EditorProps> = ({
  routeId,
  routesPanel,
  ringTypes,
  notice,
  onDirtyChange,
  onSaved,
  onDuplicated,
  onDeleted,
}) => {
  const [details, setDetails] = useState<RouteDetails>(EMPTY_DETAILS);
  const [saved, setSaved] = useState<RouteDetails>(EMPTY_DETAILS);
  const [status, setStatus] = useState<FidsTrackingRouteStatus>('draft');
  const [step, setStep] = useState<RouteStep>('center');
  const [ringType, setRingType] = useState<FidsRingType>(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const confirmationDialogRef = useRef<ConfirmationDialogRef>(null);

  const hasChanges = useMemo(
    () => !sameDetails(details, saved),
    [details, saved]
  );
  const activationIssue = activationIssueOf(details);
  // O rascunho pode ser salvo incompleto; o rastreio ativo precisa do centro e de 1 armazém.
  const blockingIssue = status === 'active' ? activationIssue : null;
  const isBusy = isLoading || isSaving;

  useEffect(() => {
    onDirtyChange(hasChanges);
  }, [hasChanges, onDirtyChange]);

  useEffect(() => {
    const defaultType = ringTypes.find(({ isDefault }) => isDefault)?.type;
    if (defaultType) setRingType(defaultType);
  }, [ringTypes]);

  const applyRoute = useCallback((route: FidsTrackingRoute) => {
    const next = detailsOf(route);
    setDetails(next);
    setSaved(next);
    setStatus(route.status);
    return next;
  }, []);

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    setErrorMessage(null);
    setFeedback(null);
    try {
      const next = applyRoute(await getFidsTrackingRoute(routeId));
      setStep(firstPendingStep(next));
    } catch (error) {
      setLoadError(apiErrorHandler(error).errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [routeId, applyRoute]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!hasChanges) return;
    const warnUnsavedChanges = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnUnsavedChanges);
    return () => window.removeEventListener('beforeunload', warnUnsavedChanges);
  }, [hasChanges]);

  const change = (update: Partial<RouteDetails>) => {
    setDetails((current) => ({ ...current, ...update }));
    setErrorMessage(null);
    setFeedback(null);
  };

  // Devolve se salvou, para o "Salvar e ativar" seguir só depois de salvar.
  const save = async (): Promise<boolean> => {
    if (!details.name.trim()) {
      setErrorMessage('Informe o nome do rastreio.');
      return false;
    }
    if (blockingIssue) {
      setErrorMessage(
        `Rastreio ativo: ${blockingIssue.charAt(0).toLowerCase()}${blockingIssue.slice(1)}`
      );
      return false;
    }
    setIsSaving(true);
    setErrorMessage(null);
    setFeedback(null);
    try {
      applyRoute(
        await updateFidsTrackingRoute(routeId, {
          name: details.name.trim(),
          description: details.description.trim() || null,
          center: details.center,
          warehouseIds: details.warehouses.map(({ id }) => id),
        })
      );
      setFeedback('Rastreio salvo.');
      notify('success', 'Rastreio salvo.');
      onSaved();
      return true;
    } catch (error) {
      setErrorMessage(apiErrorHandler(error).errorMessage);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  // Ativar, voltar para rascunho, duplicar e excluir valem para o rastreio salvo.
  const runRouteAction = async (action: () => Promise<void>) => {
    setIsSaving(true);
    setErrorMessage(null);
    setFeedback(null);
    try {
      await action();
    } catch (error) {
      setErrorMessage(apiErrorHandler(error).errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const activate = () =>
    runRouteAction(async () => {
      setStatus((await activateFidsTrackingRoute(routeId)).status);
      setFeedback('Rastreio ativado. Ele já vale no painel de chegadas.');
      onSaved();
    });

  const saveAndActivate = async () => {
    if (hasChanges && !(await save())) return;
    await activate();
  };

  const deactivate = async () => {
    const confirmed = await confirmationDialogRef.current?.openDialog({
      title: `Voltar "${saved.name}" para rascunho?`,
      message: 'O rastreio deixa de valer no painel até ser ativado de novo.',
      confirmButtonText: 'Voltar para rascunho',
    });
    if (!confirmed) return;
    await runRouteAction(async () => {
      setStatus((await deactivateFidsTrackingRoute(routeId)).status);
      setFeedback('Rastreio voltou para rascunho.');
      onSaved();
    });
  };

  const duplicate = () =>
    runRouteAction(async () => {
      const copy = await duplicateFidsTrackingRoute(routeId);
      notify('success', `Cópia criada: "${copy.name}".`);
      onDuplicated(copy.id);
    });

  const remove = async () => {
    const confirmed = await confirmationDialogRef.current?.openDialog({
      title: `Excluir o rastreio "${saved.name}"?`,
      message:
        'Os armazéns vinculados ficam sem rastreio. Esta ação não pode ser desfeita.',
      confirmButtonText: 'Excluir',
      confirmButtonMood: 'danger',
    });
    if (!confirmed) return;
    await runRouteAction(async () => {
      await deleteFidsTrackingRoute(routeId);
      notify('success', 'Rastreio excluído.');
      onDeleted();
    });
  };

  const discardChanges = () => {
    setDetails(saved);
    setErrorMessage(null);
    setFeedback(null);
  };

  const rings = ringTypes.find(({ type }) => type === ringType)?.rings ?? [];
  const isPicking = step === 'center' && !isBusy;

  const stepContent: Record<RouteStep, ReactNode> = {
    center: (
      <StepPanel step="center" onNext={() => setStep('warehouses')}>
        <p className="text-sm text-slate-700">
          {details.center
            ? 'Centro marcado. Para mudar, clique em outro ponto do mapa ou arraste o centro.'
            : 'Clique no mapa onde fica o armazém. Se precisar, busque a cidade no campo do mapa.'}
        </p>
        {details.center ? (
          <p className="text-xs text-slate-500">
            Lat. {details.center.latitude.toFixed(6)} · Long.{' '}
            {details.center.longitude.toFixed(6)}
          </p>
        ) : null}
        <p className="text-xs text-slate-500">
          Em volta do centro aparecem 4 anéis. O de 10 km, em laranja, é a
          chegada: quando o caminhão entra nele, o painel confirma a chegada. Os
          outros mostram o caminhão se aproximando e mudam com o tipo do
          veículo.
        </p>
      </StepPanel>
    ),
    warehouses: (
      <StepPanel
        step="warehouses"
        onBack={() => setStep('center')}
        onNext={() => setStep('review')}
      >
        <p className="text-sm text-slate-700">
          Escolha os armazéns (CNPJs) da unidade que usam este rastreio. Cada
          armazém fica em um rastreio só.
        </p>
        <LinkedWarehouses
          routeId={routeId}
          warehouses={details.warehouses}
          disabled={isBusy}
          onChange={(warehouses) => change({ warehouses })}
        />
      </StepPanel>
    ),
    review: (
      <StepPanel step="review" onBack={() => setStep('warehouses')}>
        <ReviewStep
          center={details.center}
          warehouseCount={details.warehouses.length}
          status={status}
          hasChanges={hasChanges}
          isBusy={isBusy}
          onGoTo={setStep}
          onSave={() => void save()}
          onSaveAndActivate={() => void saveAndActivate()}
        />
      </StepPanel>
    ),
  };

  return (
    <Scaffold>
      <S.Page>
        <S.MainPanel>
          <ListingPageHeader
            icon={<MapPinned size={22} aria-hidden="true" />}
            title={PAGE_TITLE}
            isLoading={isBusy}
            actions={
              <S.HeaderActions>
                {hasChanges ? (
                  <span className="text-xs font-semibold text-amber-700">
                    Alterações não salvas
                  </span>
                ) : null}
                <S.SecondaryButton
                  type="button"
                  disabled={!hasChanges || isSaving}
                  onClick={discardChanges}
                >
                  Descartar
                </S.SecondaryButton>
                <S.PrimaryButton
                  type="button"
                  disabled={!hasChanges || isBusy || Boolean(blockingIssue)}
                  title={blockingIssue ?? undefined}
                  onClick={() => void save()}
                >
                  <Save size={16} aria-hidden="true" />
                  {isSaving ? 'Salvando...' : 'Salvar'}
                </S.PrimaryButton>
                <TrackingRouteActions
                  status={status}
                  activationIssue={activationIssue}
                  hasChanges={hasChanges}
                  isBusy={isBusy}
                  onActivate={() => void activate()}
                  onDeactivate={() => void deactivate()}
                  onDuplicate={() => void duplicate()}
                  onDelete={() => void remove()}
                />
              </S.HeaderActions>
            }
          />

          <ConfirmationDialog ref={confirmationDialogRef} />
          {notice}
          {loadError ? (
            <div
              role="alert"
              className="flex items-center justify-between gap-3 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            >
              <span>{loadError}</span>
              <button
                type="button"
                className="font-bold underline"
                onClick={() => void load()}
              >
                Tentar novamente
              </button>
            </div>
          ) : null}
          {errorMessage ? (
            <p
              role="alert"
              className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            >
              {errorMessage}
            </p>
          ) : null}
          {feedback ? (
            <p role="status" className="text-sm font-semibold text-green-700">
              {feedback}
            </p>
          ) : null}

          <S.RouteHeader aria-label="Detalhes do rastreio">
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <div className="flex flex-wrap items-center gap-2">
                <label className="flex min-w-0 max-w-full items-center gap-2">
                  <span className="sr-only">Nome do rastreio</span>
                  <S.TitleInput
                    maxLength={120}
                    placeholder="Nome do rastreio"
                    value={details.name}
                    disabled={isBusy}
                    onChange={(event) => change({ name: event.target.value })}
                    $invalid={!isLoading && !details.name.trim()}
                  />
                  <Pencil
                    size={16}
                    aria-hidden="true"
                    className="shrink-0 text-slate-400"
                  />
                </label>
                <S.StatusBadge $status={status}>
                  {STATUS_LABELS[status]}
                </S.StatusBadge>
              </div>
              <label className="flex items-center gap-2">
                <span className="sr-only">Descrição</span>
                <S.InlineDescriptionInput
                  maxLength={500}
                  placeholder="Adicionar descrição (opcional)"
                  value={details.description}
                  disabled={isBusy}
                  onChange={(event) =>
                    change({ description: event.target.value })
                  }
                />
              </label>
            </div>
            <p className="max-w-sm text-xs text-slate-500">
              {status === 'active'
                ? 'Ativo: vale no painel para as viagens dos armazéns vinculados.'
                : 'Rascunho: pode ficar incompleto e ainda não vale no painel.'}
            </p>
          </S.RouteHeader>

          <RouteSteps
            current={step}
            done={{
              center: Boolean(details.center),
              warehouses: details.warehouses.length > 0,
              review: status === 'active' && !hasChanges,
            }}
            onSelect={setStep}
          />

          <S.EditorGrid>
            <S.RoutesColumn>{routesPanel}</S.RoutesColumn>
            <S.SideColumn>{isLoading ? null : stepContent[step]}</S.SideColumn>
            <S.MapColumn aria-label="Mapa do rastreio">
              <MapCanvas
                apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? ''}
                center={details.center}
                rings={rings}
                picking={isPicking}
                pickingHelp={
                  isPicking
                    ? details.center
                      ? 'Clique em outro ponto ou arraste o centro para mudá-lo.'
                      : 'Clique no mapa onde fica o armazém.'
                    : null
                }
                onPick={(center) => change({ center })}
                overlay={
                  details.center ? (
                    <RingLegend
                      ringTypes={ringTypes}
                      selectedType={ringType}
                      onSelectType={setRingType}
                    />
                  ) : null
                }
              />
            </S.MapColumn>
          </S.EditorGrid>
        </S.MainPanel>
      </S.Page>
    </Scaffold>
  );
};

// Lista de rastreios à esquerda; o selecionado abre no editor. Trocar de rastreio com
// alterações não salvas pede confirmação.
export const FidsTrackingRoutesPage: React.FC = () => {
  const [routes, setRoutes] = useState<FidsTrackingRouteSummary[]>([]);
  const [ringTypes, setRingTypes] = useState<FidsRingTypeOption[]>([]);
  const [ringTypesError, setRingTypesError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isNewRouteOpen, setIsNewRouteOpen] = useState(false);
  const [search, setSearch] = useState('');
  const listScrollTopRef = useRef(0);
  const hasChangesRef = useRef(false);
  const confirmationDialogRef = useRef<ConfirmationDialogRef>(null);

  const loadRoutes = useCallback(async () => {
    setLoadError(null);
    try {
      const list = await listFidsTrackingRoutes();
      setRoutes(list);
      setSelectedId((current) =>
        current !== null && list.some(({ id }) => id === current)
          ? current
          : (list[0]?.id ?? null)
      );
    } catch (error) {
      setLoadError(apiErrorHandler(error).errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadRingTypes = useCallback(async () => {
    setRingTypesError(null);
    try {
      setRingTypes(await listFidsRingTypes());
    } catch (error) {
      setRingTypesError(apiErrorHandler(error).errorMessage);
    }
  }, []);

  useEffect(() => {
    void loadRoutes();
    void loadRingTypes();
  }, [loadRoutes, loadRingTypes]);

  const handleDirtyChange = useCallback((hasChanges: boolean) => {
    hasChangesRef.current = hasChanges;
  }, []);

  const confirmLeave = async () => {
    if (!hasChangesRef.current) return true;
    const confirmed = await confirmationDialogRef.current?.openDialog({
      title: 'Descartar as alterações?',
      message: 'As alterações deste rastreio ainda não foram salvas.',
      confirmButtonText: 'Descartar',
      confirmButtonMood: 'danger',
    });
    if (confirmed) hasChangesRef.current = false;
    return Boolean(confirmed);
  };

  const selectRoute = async (id: number) => {
    if (id === selectedId || !(await confirmLeave())) return;
    setSelectedId(id);
  };

  // Recarrega a lista e abre o rastreio informado (o novo ou a cópia).
  const openRoute = async (id: number) => {
    hasChangesRef.current = false;
    await loadRoutes();
    setSelectedId(id);
  };

  const startNewRoute = async () => {
    if (await confirmLeave()) setIsNewRouteOpen(true);
  };

  const createRoute = async (name: string) => {
    setIsCreating(true);
    try {
      const route = await createFidsTrackingRoute({
        name,
        description: null,
        center: null,
        warehouseIds: [],
      });
      setIsNewRouteOpen(false);
      setSearch('');
      notify(
        'success',
        `Rastreio "${route.name}" criado. Agora marque o centro no mapa.`
      );
      await openRoute(route.id);
    } catch (error) {
      notify('error', apiErrorHandler(error).errorMessage);
    } finally {
      setIsCreating(false);
    }
  };

  const routesPanel = (
    <TrackingRouteList
      routes={routes}
      selectedId={selectedId}
      search={search}
      onSearchChange={setSearch}
      scrollTopRef={listScrollTopRef}
      isLoading={isLoading}
      isCreating={isCreating}
      loadError={loadError}
      onRetry={() => void loadRoutes()}
      onSelect={(id) => void selectRoute(id)}
      onCreate={() => void startNewRoute()}
    />
  );

  const ringTypesAlert = ringTypesError ? (
    <div
      role="alert"
      className="flex items-center justify-between gap-3 rounded border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800"
    >
      <span>Os anéis do mapa não foram carregados: {ringTypesError}</span>
      <button
        type="button"
        className="font-bold underline"
        onClick={() => void loadRingTypes()}
      >
        Tentar novamente
      </button>
    </div>
  ) : null;

  return (
    <>
      <ConfirmationDialog ref={confirmationDialogRef} />
      {isNewRouteOpen ? (
        <NewTrackingRouteDialog
          isCreating={isCreating}
          onCancel={() => setIsNewRouteOpen(false)}
          onCreate={(name) => void createRoute(name)}
        />
      ) : null}
      {selectedId === null ? (
        <Scaffold>
          <S.Page>
            <S.MainPanel>
              <ListingPageHeader
                icon={<MapPinned size={22} aria-hidden="true" />}
                title={PAGE_TITLE}
                isLoading={isLoading || isCreating}
              />
              {!isLoading && !loadError && routes.length === 0 ? (
                <WelcomePanel
                  isCreating={isCreating}
                  onCreate={() => setIsNewRouteOpen(true)}
                />
              ) : (
                <S.EditorGrid>
                  <S.RoutesColumn>{routesPanel}</S.RoutesColumn>
                </S.EditorGrid>
              )}
            </S.MainPanel>
          </S.Page>
        </Scaffold>
      ) : (
        <TrackingRouteEditor
          key={selectedId}
          routeId={selectedId}
          routesPanel={routesPanel}
          ringTypes={ringTypes}
          notice={ringTypesAlert}
          onDirtyChange={handleDirtyChange}
          onSaved={() => void loadRoutes()}
          onDuplicated={(id) => void openRoute(id)}
          onDeleted={() => {
            hasChangesRef.current = false;
            void loadRoutes();
          }}
        />
      )}
    </>
  );
};
